import './coapplicantIncomeDetailsPage.scss';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { isEmpty, toString, uniqueId } from 'lodash';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { incomeFrequencyOptions } from '../../../store/applicantStore/constants/incomeFrequencyOptions';
import { incomeTypeOptions } from '../../../store/applicantStore/constants/incomeTypeOptions';
import { IncomeTypeEnum } from '../../../store/applicantStore/enums/IncomeTypeEnum';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { inputEventHandler } from '../../../utils/component/inputEventHandler';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();
  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    delayError: 200,
  });

  const {
    coapplicantAdditionalIncomeAmount,
    coapplicantAdditionalIncomeDurationMonths,
    coapplicantAdditionalIncomeDurationYears,
    coapplicantAdditionalIncomeEmployerName,
    coapplicantAdditionalIncomeFrequency,
    coapplicantAdditionalIncomeJobTitle,
    coapplicantAdditionalIncomeType,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantAdditionalIncomeAmount,
    setCoapplicantAdditionalIncomeDurationMonths,
    setCoapplicantAdditionalIncomeDurationYears,
    setCoapplicantAdditionalIncomeEmployerName,
    setCoapplicantAdditionalIncomeFrequency,
    setCoapplicantAdditionalIncomeJobTitle,
    setCoapplicantAdditionalIncomeType,
    syncCoapplicant,
  } = useCoapplicantStore();

  const previousStep = (): void => {
    navigate(previous());
  };

  const incomeTypeValidation = {
    required: t('validation.incomeType.required'),
  };

  const incomeFrequencyValidation = {
    required: t('validation.incomeFrequency.required'),
  };

  const incomeAmountValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.incomeAmount.required'),
    min: {
      value: 0,
      message: t('validation.incomeAmount.min', { min: '$0' }),
    },
  };

  const incomeEmployerNameValidation = {
    required: t('validation.employerName.required'),
  };

  const incomeJobTitleValidation = {
    required: t('validation.jobTitle.required'),
  };

  const incomeDurationYearsValidation = {
    required: t('validation.durationYears.required'),
  };

  const incomeDurationMonthsValidation = {
    required: t('validation.durationMonths.required'),
  };

  const showSecondaryEmploymentFields =
    watch('incomeTypeValue', coapplicantAdditionalIncomeType) ===
    IncomeTypeEnum.SECONDARY_EMPLOYMENT;

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      incomeTypeValue,
      incomeDurationMonthsValue,
      incomeDurationYearsValue,
      incomeEmployerNameValue,
      incomeFrequencyValue,
      incomeJobTitleValue,
      incomeAmountValue,
    } = values;
    if (!incomeTypeValue || !incomeFrequencyValue || !incomeAmountValue) {
      return;
    }

    setCoapplicantAdditionalIncomeAmount(incomeAmountValue);
    setCoapplicantAdditionalIncomeFrequency(incomeFrequencyValue);
    setCoapplicantAdditionalIncomeType(incomeTypeValue);

    if (showSecondaryEmploymentFields) {
      setCoapplicantAdditionalIncomeDurationMonths(incomeDurationMonthsValue);
      setCoapplicantAdditionalIncomeDurationYears(incomeDurationYearsValue);
      setCoapplicantAdditionalIncomeEmployerName(incomeEmployerNameValue);
      setCoapplicantAdditionalIncomeJobTitle(incomeJobTitleValue);
    } else {
      setCoapplicantAdditionalIncomeDurationMonths(null);
      setCoapplicantAdditionalIncomeDurationYears(null);
      setCoapplicantAdditionalIncomeEmployerName(null);
      setCoapplicantAdditionalIncomeJobTitle(null);
    }

    if (isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-income-details-page" sx={theme.pageLayout}>
        <header
          data-testid={locator.pages.coapplicantIncomeDetails.header.root}
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.coapplicantIncomeDetails.header.previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.coapplicantIncomeDetails.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.coapplicantIncomeDetails.progress.root}
            value={useWorkflowStore.getState().computed.progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.coapplicantIncomeDetails.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantIncomeDetails.form.h1}>
                {t('CoapplicantIncomeDetailsPage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantIncomeDetails.form.h2}>
                {t('CoapplicantIncomeDetailsPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantIncomeDetails.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <TextField
                    {...register('incomeTypeValue', incomeTypeValidation)}
                    SelectProps={{
                      IconComponent: ExpandMoreRoundedIcon,
                    }}
                    error={!isEmpty(errors.incomeTypeValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantIncomeDetails.form
                          .incomeTypeSelect,
                    }}
                    label={t('CoapplicantIncomeDetailsPage.incomeType')}
                    value={watch(
                      'incomeTypeValue',
                      coapplicantAdditionalIncomeType || '',
                    )}
                    fullWidth
                    select
                  >
                    <MenuItem value="" disabled>
                      {t('CoapplicantIncomeDetailsPage.incomeType')}
                    </MenuItem>

                    {incomeTypeOptions.map(({ name, value }) => {
                      return (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {!isEmpty(errors.incomeTypeValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantIncomeDetails.form
                          .incomeTypeSelectError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.incomeTypeValue.message)}
                    </span>
                  )}
                </div>

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'incomeEmployerNameValue',
                        incomeEmployerNameValidation,
                      )}
                      error={!isEmpty(errors.employerNameValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.coapplicantIncomeDetails.form
                            .employerNameInput,
                      }}
                      label={t('CoapplicantIncomeDetailsPage.employerName')}
                      value={watch(
                        'incomeEmployerNameValue',
                        coapplicantAdditionalIncomeEmployerName || '',
                      )}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.incomeEmployerNameValue) && (
                      <span
                        data-testid={
                          locator.pages.coapplicantIncomeDetails.form
                            .employerNameInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.incomeEmployerNameValue.message)}
                      </span>
                    )}
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'incomeJobTitleValue',
                        incomeJobTitleValidation,
                      )}
                      error={!isEmpty(errors.jobTitleValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.coapplicantIncomeDetails.form
                            .jobTitleInput,
                      }}
                      label={t('CoapplicantIncomeDetailsPage.jobTitle')}
                      value={watch(
                        'incomeJobTitleValue',
                        coapplicantAdditionalIncomeJobTitle || '',
                      )}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.incomeJobTitleValue) && (
                      <span
                        data-testid={
                          locator.pages.coapplicantIncomeDetails.form
                            .jobTitleInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.incomeJobTitleValue.message)}
                      </span>
                    )}
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div
                    className="inline-inputs"
                    style={{ marginTop: '1.5rem' }}
                  >
                    <div className="input-half">
                      <TextField
                        {...register(
                          'incomeDurationYearsValue',
                          incomeDurationYearsValidation,
                        )}
                        SelectProps={{
                          IconComponent: ExpandMoreRoundedIcon,
                        }}
                        error={!isEmpty(errors.durationYearsValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantIncomeDetails.form
                              .yearSelect,
                        }}
                        label={t('CoapplicantIncomeDetailsPage.durationYears')}
                        value={watch(
                          'incomeDurationYearsValue',
                          coapplicantAdditionalIncomeDurationYears,
                        )}
                        fullWidth
                        select
                      >
                        <MenuItem value="" disabled>
                          {t('CoapplicantIncomeDetailsPage.durationYears')}
                        </MenuItem>

                        {Array.from({ length: 11 }, (_, index) => {
                          return (
                            <MenuItem
                              key={uniqueId(`duration-years-${index}`)}
                              value={index}
                            >
                              {index}
                            </MenuItem>
                          );
                        })}
                      </TextField>

                      {!isEmpty(errors.incomeDurationYearsValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantIncomeDetails.form
                              .yearSelectError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.incomeDurationYearsValue.message)}
                        </span>
                      )}
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register(
                          'incomeDurationMonthsValue',
                          incomeDurationMonthsValidation,
                        )}
                        SelectProps={{
                          IconComponent: ExpandMoreRoundedIcon,
                        }}
                        error={!isEmpty(errors.durationMonthsValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantIncomeDetails.form
                              .monthSelect,
                        }}
                        label={t('CoapplicantIncomeDetailsPage.durationMonths')}
                        value={watch(
                          'incomeDurationMonthsValue',
                          coapplicantAdditionalIncomeDurationMonths,
                        )}
                        fullWidth
                        select
                      >
                        <MenuItem value="" disabled>
                          {t('CoapplicantIncomeDetailsPage.durationMonths')}
                        </MenuItem>

                        {Array.from({ length: 12 }, (_, index) => {
                          return (
                            <MenuItem
                              key={uniqueId(`duration-months-${index}`)}
                              value={index}
                            >
                              {index}
                            </MenuItem>
                          );
                        })}
                      </TextField>

                      {!isEmpty(errors.incomeDurationMonthsValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantIncomeDetails.form
                              .monthSelectError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.incomeDurationMonthsValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register(
                      'incomeFrequencyValue',
                      incomeFrequencyValidation,
                    )}
                    SelectProps={{
                      IconComponent: ExpandMoreRoundedIcon,
                    }}
                    error={!isEmpty(errors.incomeFrequencyValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantIncomeDetails.form
                          .frequencySelect,
                    }}
                    label={t('CoapplicantIncomeDetailsPage.incomeFrequency')}
                    value={watch(
                      'incomeFrequencyValue',
                      coapplicantAdditionalIncomeFrequency || '',
                    )}
                    fullWidth
                    select
                  >
                    <MenuItem value="" disabled>
                      {t('CoapplicantIncomeDetailsPage.incomeFrequency')}
                    </MenuItem>

                    {incomeFrequencyOptions.map(({ name, value }) => {
                      return (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {!isEmpty(errors.incomeFrequencyValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantIncomeDetails.form
                          .frequencySelectError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.incomeFrequencyValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register('incomeAmountValue', incomeAmountValidation)}
                    defaultValue={inputEventHandler.currency.formatter(
                      coapplicantAdditionalIncomeAmount,
                    )}
                    error={!isEmpty(errors.incomeAmountValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantIncomeDetails.form.amountInput,
                      inputMode: 'numeric',
                    }}
                    label={t('CoapplicantIncomeDetailsPage.incomeAmount')}
                    type="text"
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'incomeAmountValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'incomeAmountValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'incomeAmountValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.incomeAmountValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantIncomeDetails.form
                          .amountInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.incomeAmountValue.message)}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.coapplicantIncomeDetails.form.submitButton
                  }
                  disabled={!isValid}
                  loading={isSyncingCoapplicant}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('CoapplicantIncomeDetailsPage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantIncomeDetails.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantIncomeDetailsPage';
